import axios from "axios";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { authApi } from "../api/auth";
import { getStoredToken, setStoredToken } from "../api/client";
import type { User } from "../api/types";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(getStoredToken());
  const loading = ref(false);

  const isAuthenticated = computed(() => !!user.value && !!token.value);
  const isAdmin = computed(() => user.value?.is_admin === true);

  const LEVEL_RANK: Record<string, number> = { none: 0, read: 1, write: 2, admin: 3 };
  function hasAccess(module: string, required: "read" | "write" | "admin" = "read"): boolean {
    if (user.value?.is_admin) return true;
    const lvl = user.value?.modules?.[module] ?? "none";
    return (LEVEL_RANK[lvl] ?? 0) >= (LEVEL_RANK[required] ?? 1);
  }

  async function login(email: string, password: string) {
    loading.value = true;
    try {
      const res = await authApi.login(email, password);
      token.value = res.access_token;
      setStoredToken(res.access_token);
      user.value = res.user;
      return res;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    await authApi.logout();
    user.value = null;
    token.value = null;
    setStoredToken(null);
  }

  /**
   * 起動時: localStorage にトークンがあれば /auth/me で復元。
   * 401/403 のみトークン破棄 (本当に無効)。
   * ネットワーク障害やサーバ 5xx ではトークンを保持し、後続リクエストで再試行できるようにする。
   *
   * このプロミスは router.beforeEach から await されるため、
   * 起動 → 初回ナビゲーション → guard 判定 が「restore 完了後」に走ることを保証する。
   */
  let restorePromise: Promise<void> | null = null;
  function ready(): Promise<void> {
    return restorePromise ?? Promise.resolve();
  }
  async function restore() {
    if (restorePromise) return restorePromise;
    restorePromise = (async () => {
      const stored = getStoredToken();
      if (!stored) return;
      token.value = stored;
      try {
        user.value = await authApi.me();
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401 || status === 403) {
          user.value = null;
          token.value = null;
          setStoredToken(null);
        }
        // それ以外 (ネットワーク断 / 5xx) はトークン保持、user は未設定のまま
      }
    })();
    return restorePromise;
  }

  function setUser(u: User) {
    user.value = u;
  }

  return { user, token, loading, isAuthenticated, isAdmin, hasAccess, login, logout, restore, ready, setUser };
});
